
import React, { useMemo, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { type PricingTier } from "@/hooks/useCheckout";
import { Plan } from "@/types/api";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "../AppSidebar";
import { Footer } from "../Footer";
import { SiteHeader } from "../SiteHeader";
import { usePlansData, useBillingOperations } from "@/hooks/queries";

// Pricing information for different plans
const planPricing = {
  free: { price: 0, period: "month" },
  pro: { price: 20, period: "month" },
  max: { price: 100, period: "month" },
};

// Skeleton card component for loading state
const SkeletonCard: React.FC = () => {
  return (
    <Card className="group relative overflow-hidden transition-all duration-300 ease-out hover:shadow-xl hover:shadow-slate-900/10 dark:hover:shadow-slate-900/30 hover:-translate-y-1 flex flex-col h-full border-0 bg-white dark:bg-slate-900 shadow-lg shadow-slate-900/5 dark:shadow-slate-900/20">
      <CardHeader className="pb-6 relative flex-shrink-0">
        <div className="flex items-start justify-between mb-6">
          <div className="p-2.5 rounded-2xl bg-slate-100 dark:bg-slate-800/40">
            <div className="h-5 w-5">
              <Skeleton className="h-5 w-5" />
            </div>
          </div>
        </div>
        
        <CardTitle className="text-xl font-light tracking-tight mb-4 text-slate-900 dark:text-slate-100">
          <Skeleton className="h-6 w-16 bg-slate-200 dark:bg-slate-700" />
        </CardTitle>

        <div className="space-y-1">
          <div className="flex items-baseline gap-1">
            <Skeleton className="h-8 w-16 bg-slate-200 dark:bg-slate-700" />
            <Skeleton className="h-4 w-10 bg-slate-200 dark:bg-slate-700" />
          </div>
        </div>
      </CardHeader>

      <CardContent className="relative flex-grow pb-6">
        <ul className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <li key={index} className="flex items-start gap-3">
              <Skeleton className="w-4 h-4 mt-0.5 flex-shrink-0 bg-slate-200 dark:bg-slate-700" />
              <Skeleton className="h-4 flex-1 bg-slate-200 dark:bg-slate-700" />
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter className="pt-0 relative flex-shrink-0">
        <Skeleton className="w-full h-12 rounded-xl bg-slate-200 dark:bg-slate-700" />
      </CardFooter>
    </Card>
  );
};

// Function to transform API Plan data to PricingTier format
const transformPlanToPricingTier = (plan: Plan): PricingTier => {
  const pricing = planPricing[plan.name as keyof typeof planPricing] || { price: 0, period: "month" };
  
  // Use features directly from API
  const features = plan.features || [
    `${plan.limits.requestsPerDay} requests per day`,
    `${plan.limits.models.length} AI models available`
  ];

  // Determine button text based on plan (always for new users)
  let buttonText = null;
  if (plan.name === "free") {
    buttonText = "Get Started Free";
  } else {
    buttonText = "Subscribe";
  }

  return {
    id: plan.id,
    name: plan.name.charAt(0).toUpperCase() + plan.name.slice(1),
    price: pricing.price,
    period: pricing.period,
    priceId: plan.id,
    features,
    popular: false, // Remove popular distinction
    icon: <Sparkles className="h-6 w-6" />, // Use same icon for all
    gradient: "from-slate-600 to-slate-600", // Consistent gradient
    buttonText,
    billingAvailable: plan.name !== "free", // Only paid plans have billing management
  };
};

// Pricing content component that contains all the pricing UI
const PricingContent: React.FC<{
  pricingTiers: PricingTier[];
  isLoading: boolean;
  error: string | null;
  handleSubscribe: (tier: PricingTier) => void;
  loadingTier: string | null;
  activePlan?: Plan | null;
  handleBillingPortal?: (tierId: string) => void;
  billingLoadingTier?: string | null;
  billingError?: string | null;
}> = ({ pricingTiers, isLoading, error, handleSubscribe, loadingTier, activePlan, handleBillingPortal, billingLoadingTier, billingError }) => {
  // Show loading state while fetching plans
  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Pricing Cards Grid - Skeleton State */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      </div>
    );
  }

  // Show message when no plans are available
  if (pricingTiers.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950/50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">📋</div>
          <h2 className="text-2xl font-light tracking-tight text-slate-900 dark:text-white mb-2">No Plans Available</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            {error ? 'Failed to load pricing plans. Please try again later.' : 'No pricing plans are currently configured.'}
          </p>
          <Button onClick={() => window.location.reload()} variant="outline" className="rounded-xl">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 text-center">
          {error}
        </div>
      )}

      {/* Pricing Cards Grid */}
     { activePlan && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {pricingTiers.map((tier) => {
          // Check if this tier matches the user's active plan by name
          // Since user might have Clerk plan but we're showing Stripe equivalents
          const isActivePlan = activePlan && (
            activePlan.id === tier.id || 
            activePlan.name.toLowerCase() === tier.name.toLowerCase()
          );
          
          return (
            <Card
              key={tier.id}
              className={cn(
                "group relative overflow-hidden transition-all duration-300 ease-out hover:shadow-xl hover:shadow-slate-900/10 dark:hover:shadow-slate-900/30 hover:-translate-y-1 flex flex-col h-full border-0",
                isActivePlan
                  ? "bg-gradient-to-br from-emerald-50 via-green-50/50 to-emerald-50 dark:from-emerald-950/30 dark:via-green-950/20 dark:to-emerald-950/30 shadow-xl shadow-emerald-900/5 dark:shadow-emerald-900/20"
                  : "bg-white dark:bg-slate-900 shadow-lg shadow-slate-900/5 dark:shadow-slate-900/20"
              )}
            >
              {/* Gradient accent border */}
              {isActivePlan && (
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-green-400" />
              )}

              {/* Subtle background pattern */}
              {isActivePlan && (
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#10b98108_1px,transparent_1px),linear-gradient(to_bottom,#10b98108_1px,transparent_1px)] bg-[size:12px_12px]" />
              )}

              {/* Status badges */}
              {isActivePlan && (
                <div className="absolute top-4 right-4">
                  <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-full">
                    ACTIVE
                  </span>
                </div>
              )}

              <CardHeader className="pb-6 relative flex-shrink-0">
                                  <div className="flex items-start justify-between mb-6">
                    <div className={cn(
                      "p-2.5 rounded-2xl transition-all duration-300",
                      isActivePlan 
                        ? "bg-emerald-100 dark:bg-emerald-900/40 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/60"
                        : "bg-slate-100 dark:bg-slate-800/40 group-hover:bg-slate-200 dark:group-hover:bg-slate-800/60"
                    )}>
                      <div className={cn(
                        "h-5 w-5",
                        isActivePlan 
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-slate-600 dark:text-slate-400"
                      )}>
                        {tier.icon}
                      </div>
                    </div>
                  </div>
                
                <CardTitle className={cn(
                  "text-xl font-light tracking-tight mb-4",
                  isActivePlan 
                    ? "text-emerald-900 dark:text-emerald-100"
                    : "text-slate-900 dark:text-slate-100"
                )}>
                  {tier.name}
                </CardTitle>

                <div className="space-y-1">
                  <div className="flex items-baseline gap-1">
                    <span className={cn(
                      "text-3xl font-light tracking-tight",
                      isActivePlan 
                        ? "text-emerald-900 dark:text-emerald-100"
                        : "text-slate-900 dark:text-slate-100"
                    )}>
                      ${tier.price}
                    </span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">/{tier.period}</span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="relative flex-grow pb-6">
                <ul className="space-y-3">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className={cn(
                        "w-4 h-4 mt-0.5 flex-shrink-0",
                        isActivePlan 
                          ? "text-emerald-500"
                          : "text-slate-500"
                      )} />
                      <span className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="pt-0 relative flex-shrink-0">
                {isActivePlan ? (
                  <div className="w-full space-y-3">
                    {handleBillingPortal && tier.billingAvailable && (
                      <Button
                        onClick={() => handleBillingPortal(tier.id)}
                        disabled={billingLoadingTier === tier.id}
                        className="w-full h-12 font-medium px-6 rounded-xl transition-all duration-200 ease-out transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 shadow-slate-900/25 hover:shadow-slate-900/30"
                      >
                        {(() => {
                          const isBillingLoading = billingLoadingTier === tier.id;
                          console.log('🔍 Change Plan button render - billingLoadingTier:', billingLoadingTier, 'tier.id:', tier.id, 'isLoading:', isBillingLoading);
                                                     return isBillingLoading ? (
                             <div className="flex items-center justify-center">
                               <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-2" />
                               Loading...
                             </div>
                           ) : (
                             "Change Plan"
                           );
                        })()}
                      </Button>
                    )}
                    {billingError && (
                      <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-xs text-red-700 dark:text-red-300">{billingError}</p>
                      </div>
                    )}
                  </div>
                ) : activePlan && activePlan.name !== "free" ? (
                  // User has subscription but this is not their active plan - show no button
                  null
                ) : tier.buttonText && tier.buttonText.length > 0 ? (
                  <Button
                    onClick={() => handleSubscribe(tier)}
                    disabled={loadingTier === tier.id}
                    className="w-full h-12 font-medium px-6 rounded-xl transition-all duration-200 ease-out transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 shadow-slate-900/25 hover:shadow-slate-900/30"
                  >
                    {(() => {
                      const isLoading = loadingTier === tier.id;
                      return isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-2" />
                          Loading...
                        </div>
                      ) : (
                        tier.buttonText
                      );
                    })()}
                  </Button>
                ) : null}
              </CardFooter>
            </Card>
          );
        })}
      </div>}
    </div>
  );
};

export function StripeBillingPage() {
  // Use optimized React Query hooks
  const { plans, activePlan, isLoading, error, getFilteredPlans } = usePlansData();
  const { createCheckout, openBillingPortal, checkoutError, billingPortalError } = useBillingOperations();
  const { isSignedIn: userSignedIn } = useUser();
  
  // Local state for tier-specific loading indicators
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [billingLoadingTier, setBillingLoadingTier] = useState<string | null>(null);

  // Remove this useEffect - useUserPlanQuery already handles authentication state
  // and will automatically fetch when user is signed in and loaded

  // Handle subscription with tier-specific loading
  const handleSubscribe = async (tier: PricingTier) => {
    setLoadingTier(tier.id);
    try {
      await createCheckout(tier.priceId);
    } catch (error) {
      console.error('Checkout error:', error);
      setLoadingTier(null);
    } finally {
      setTimeout(() => {
        setLoadingTier(null);
      }, 5000);
    }
  };


  // Handle billing portal with tier-specific loading
  const handleBillingPortal = async (tierId: string) => {
    setBillingLoadingTier(tierId);
    
    try {
      await openBillingPortal();
    } catch (error) {
      console.error('Billing portal error:', error);
      setBillingLoadingTier(null);
    } finally {
      setTimeout(() => {
        setBillingLoadingTier(null);
      }, 5000);
    }
  };

  // Transform API plans to PricingTier format - always use Stripe plans
  const pricingTiers = useMemo(() => {
    if (plans?.length > 0) {
      try {
        const stripePlans = getFilteredPlans(plans, "stripe");
        return stripePlans.map(plan => transformPlanToPricingTier(plan));
      } catch (error) {
        console.error("Error transforming plans to PricingTier:", error);
        return [];
      }
    }
    return [];
  }, [plans, getFilteredPlans]); // Remove activePlan dependency as it's not used in the calculation

  // If user is signed in, show with sidebar following Dashboard pattern
  if (userSignedIn) {
    return (
      <SidebarProvider>
        <AppSidebar variant="inset" />
        <SidebarInset className="flex flex-col min-h-screen">
          <SiteHeader className="sticky top-0 z-10" />

          {/* Enhanced background matching Dashboard */}
          <div className="flex-1 bg-gradient-to-br from-slate-50 via-white to-slate-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950/50 relative flex flex-col">
            {/* Refined background pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:16px_16px]" />

            <main className="relative flex-1 flex flex-col overflow-auto">
              <div className="max-w-7xl mx-auto px-6 py-6 space-y-6 w-full">
                {/* Page Header with Apple-style typography */}
                <section className="space-y-4 animate-in fade-in-0 duration-500 ease-out flex justify-center items-center">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-2 max-w-3xl">
                      <h1 className="text-3xl font-light tracking-tight text-slate-900 dark:text-slate-100">
                        Pricing Plans
                      </h1>
                      <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                        Choose the plan that fits your needs. All plans include core features with flexible scaling options.
                      </p>
                    </div>

                  </div>
                </section>

                {/* Pricing Content with proper animation */}
                <section className="animate-in fade-in-0 duration-700 ease-out">
                  <PricingContent
                    key={`pricing-${activePlan?.id || 'no-plan'}-${activePlan?.name || 'null'}`}
                    pricingTiers={pricingTiers}
                    isLoading={isLoading}
                    error={error || checkoutError}
                    handleSubscribe={handleSubscribe}
                    loadingTier={loadingTier}
                    activePlan={activePlan}
                    handleBillingPortal={handleBillingPortal}
                    billingLoadingTier={billingLoadingTier}
                    billingError={billingPortalError}
                  />
                </section>
              </div>
            </main>

            <Footer />
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // For unauthenticated users, show standalone pricing page
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950/50">
      {/* Refined background pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:16px_16px]" />
      
      <div className="relative">
        <main className="max-w-7xl mx-auto px-6 py-12 space-y-8">
          {/* Page Header with Apple-style typography */}
          <section className="text-center space-y-4 animate-in fade-in-0 duration-500 ease-out">
            <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-slate-900 dark:text-slate-100 mb-6">
              Choose Your Plan
            </h1>
            <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto mb-8">
              Powerful AI capabilities designed to scale with your business. Simple pricing, no hidden fees.
            </p>
          </section>

          {/* Pricing Content */}
          <section className="animate-in fade-in-0 duration-700 ease-out">
            <PricingContent
              key={`pricing-unauthenticated-${plans?.length || 0}`}
              pricingTiers={pricingTiers}
              isLoading={isLoading}
              error={error || checkoutError}
              handleSubscribe={handleSubscribe}
              loadingTier={loadingTier}
              activePlan={null}
              handleBillingPortal={undefined}
              billingLoadingTier={null}
              billingError={null}
            />
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
}
